/**
 * Brand Space error mapping
 *
 * Turns the backend's Brand Space errors (create / pause / reactivate a brand) into a small typed
 * value the UI can render as an inline notice with an upsell, instead of a raw error toast.
 *
 * Backend response shape (error middleware): { success: false, error: <translated message>, code, ...extra }
 *   403 agency.brand_space_full               + { used, total }
 *   409 agency.brand_hidden_by_plan
 *   409 agency.brand_reactivation_cooldown    + { availableAt: ISO }
 *   403 package.limit_reached                 (create at the limit; legacy code)
 */

import axios from 'axios';

export const BRAND_SPACE_FULL_CODE = 'agency.brand_space_full';
export const BRAND_HIDDEN_BY_PLAN_CODE = 'agency.brand_hidden_by_plan';
export const BRAND_REACTIVATION_COOLDOWN_CODE = 'agency.brand_reactivation_cooldown';
export const PACKAGE_LIMIT_REACHED_CODE = 'package.limit_reached';

/** What the user was trying to do when the limit was hit */
export type BrandSpaceAction = 'create' | 'reactivate';

export type BrandSpaceIssue =
  | { kind: 'full'; used: number | null; total: number | null }
  | { kind: 'hiddenByPlan' }
  | { kind: 'cooldown'; availableAt: string | null };

interface BrandSpaceErrorBody {
  code?: string;
  used?: unknown;
  total?: unknown;
  availableAt?: unknown;
}

const asNumberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * Returns the matching issue for a Brand Space error, or null when the error is something else
 * (callers then fall back to their normal error handling).
 */
export const parseBrandSpaceIssue = (error: unknown): BrandSpaceIssue | null => {
  if (!axios.isAxiosError(error)) return null;
  const body = error.response?.data as BrandSpaceErrorBody | undefined;
  switch (body?.code) {
    case BRAND_SPACE_FULL_CODE:
    case PACKAGE_LIMIT_REACHED_CODE:
      return { kind: 'full', used: asNumberOrNull(body.used), total: asNumberOrNull(body.total) };
    case BRAND_HIDDEN_BY_PLAN_CODE:
      return { kind: 'hiddenByPlan' };
    case BRAND_REACTIVATION_COOLDOWN_CODE:
      return {
        kind: 'cooldown',
        availableAt: typeof body.availableAt === 'string' ? body.availableAt : null,
      };
    default:
      return null;
  }
};
